import type { GroceryListResponse } from './api';

type Change = { checked?: boolean; quantity?: number | null };
type Pending = { change: Change; failed: boolean };
type Snapshot = { data: GroceryListResponse | null; error: string | null; pending: number; cached: boolean };
export type SavedShopping = {
  data: GroceryListResponse | null;
  edits: [string, Pending][];
  savedAt: number;
};
export type ShoppingPersistence = {
  load(path: string): SavedShopping | null;
  save(path: string, state: SavedShopping): void;
  clear(path: string): void;
};
type Transport = {
  read(path: string, etag: string | null): Promise<{ data: GroceryListResponse | null; etag: string | null }>;
  write(list: string, item: string, change: Change): Promise<unknown>;
};

/** Lives outside React: navigating away cannot cancel a user's accepted tap.
 * Only server snapshots advance the ETag. Per-item writes are serialized and
 * newer local intent remains overlaid until its own acknowledgment arrives. */
export class ShoppingStore {
  private state: Snapshot = { data: null, error: null, pending: 0, cached: false };
  private listeners = new Set<() => void>();
  private edits = new Map<string, Pending>();
  private writing = new Set<string>();
  private etag: string | null = null;
  private epoch = 0;
  private loading: Promise<void> | null = null;
  private newerList: GroceryListResponse | null = null;
  path: string;
  pin(listId: string) {
    const next = `/lists/${listId}`;
    if (this.path !== next) {
      this.path = next; this.etag = null; this.epoch++;
      this.persist();
    }
  }
  private transport: Transport;
  private persistence?: ShoppingPersistence;

  constructor(path: string, transport: Transport, persistence?: ShoppingPersistence) {
    this.path = path; this.transport = transport; this.persistence = persistence;
    const saved = persistence?.load(path);
    if (saved?.data && (saved.edits.length || Date.now() - saved.savedAt < 14 * 86400000)) {
      this.edits = new Map(saved.edits.map(([id, edit]) => [id, { ...edit, failed: false }]));
      this.state = { data: saved.data, error: null, pending: this.edits.size, cached: true };
    }
  }
  snapshot = () => this.state;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private publish(next: Partial<Snapshot> = {}) {
    this.state = { ...this.state, ...next, pending: this.edits.size };
    this.persist();
    for (const listener of this.listeners) listener();
  }
  private persist() {
    try {
      this.persistence?.save(this.path, {
        data: this.state.data, edits: [...this.edits], savedAt: Date.now(),
      });
    } catch {
      // Browser storage can be disabled or full. The live queue still works;
      // the screen accurately continues to say changes are not yet shared.
    }
  }
  private overlay(data: GroceryListResponse): GroceryListResponse {
    return { ...data, items: data.items.map(item => {
      const change = this.edits.get(item.id)?.change;
      return change ? { ...item, ...('quantity' in change ? { quantity: change.quantity ?? null } : {}),
        ...('checked' in change ? { checked: change.checked ? 1 : 0 } : {}) } : item;
    }) };
  }
  refresh = async (force = false): Promise<void> => {
    if (force) { this.etag = null; this.epoch++; }
    if (this.loading) { await this.loading; if (force) return this.refresh(); return; }
    const epoch = this.epoch;
    this.loading = (async () => {
      try {
        const result = await this.transport.read(this.path, this.etag);
        if (epoch !== this.epoch) return;
        if (result.data) {
          if (this.edits.size && this.state.data?.list?.id !== result.data.list?.id) {
            this.newerList = result.data;
            this.publish({ error: 'A new shopping list is active. Review these unsaved changes, then switch to the new list.' });
            return;
          }
          if (this.edits.size && result.data.list?.status !== 'active') {
            this.publish({ error: 'This shopping list has ended. Review these unsaved changes before switching lists.' });
            return;
          }
          this.etag = result.etag;
          this.publish({ data: this.overlay(result.data), cached: false,
            error: this.hasFailed() ? 'Some changes have not saved. Retry when your connection is back.' : null });
          for (const id of this.edits.keys()) if (!this.edits.get(id)?.failed) void this.flush(id);
        } else if (!this.hasFailed()) this.publish({ error: null });
      } catch (err) { this.publish({ error: err instanceof Error ? err.message : 'Could not load the list.' }); }
    })();
    try { await this.loading; } finally { this.loading = null; }
  };
  private hasFailed() { return [...this.edits.values()].some(edit => edit.failed); }
  change = (item: string, change: Change) => {
    if (!this.state.data?.list) return;
    this.edits.set(item, { change: { ...this.edits.get(item)?.change, ...change }, failed: false });
    this.publish({ data: this.overlay(this.state.data), error: null });
    void this.flush(item);
  };
  retry = async () => {
    if (this.path === '/lists/current') {
      await this.refresh(true);
      if (this.state.error?.startsWith('A new shopping list is active')) return;
    }
    for (const edit of this.edits.values()) edit.failed = false;
    await Promise.all([...this.edits.keys()].map(id => this.flush(id)));
    await this.refresh(true);
  };
  switchToCurrent = async () => {
    this.edits.clear();
    this.etag = null; this.epoch++;
    this.publish({ error: null, data: this.newerList, cached: false });
    this.newerList = null;
    await this.refresh(true);
  };
  private async flush(item: string) {
    if (this.writing.has(item)) return;
    const list = this.state.data?.list?.id;
    if (!list) return;
    this.writing.add(item);
    try {
      while (this.edits.has(item)) {
        const edit = this.edits.get(item)!;
        try {
          await this.transport.write(list, item, edit.change);
          this.epoch++;
          this.etag = null;
          if (this.edits.get(item) === edit) this.edits.delete(item);
          this.publish();
        } catch (err) {
          const latest = this.edits.get(item);
          if (latest) latest.failed = true;
          this.publish({ error: err instanceof Error ? `Not saved: ${err.message}` : 'That change did not save. Retry when connected.' });
          break;
        }
      }
    } finally { this.writing.delete(item); }
    await this.refresh(true);
  }
}

